import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { SystemHealthMetric } from "@/lib/schema";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Cpu,
  Database,
  RefreshCw,
  Server,
  Zap,
} from "lucide-react";

const statusIcons = {
  healthy: CheckCircle,
  warning: AlertTriangle,
  attention: AlertCircle,
};

const statusColors = {
  healthy: "text-green-600 dark:text-green-400",
  warning: "text-amber-600 dark:text-amber-400",
  attention: "text-rose-600 dark:text-rose-400",
};

const statusBgColors = {
  healthy: "bg-green-100 dark:bg-green-900/30",
  warning: "bg-amber-100 dark:bg-amber-900/30",
  attention: "bg-rose-100 dark:bg-rose-900/30",
};

const metricIcons: Record<string, React.ElementType> = {
  "nova-ai": Cpu,
  "api-health": Server,
  database: Database,
  engagement: Zap,
};

export default function SystemHealth() {
  const {
    data: metrics,
    isLoading,
    refetch,
    isFetching,
  } = useQuery<SystemHealthMetric[]>({
    queryKey: ["/api/health/metrics"],
  });

  const overallHealth = metrics
    ? Math.round(metrics.reduce((sum, m) => sum + m.value, 0) / metrics.length)
    : 0;

  const overallStatus =
    overallHealth >= 90
      ? "healthy"
      : overallHealth >= 70
        ? "warning"
        : "attention";
  const OverallIcon = statusIcons[overallStatus];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight">
            System Health
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitor platform performance and Aeos AI status
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="button-refresh-health"
        >
          <RefreshCw
            className={cn("h-4 w-4 mr-2", isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div
              className={cn(
                "flex h-16 w-16 items-center justify-center rounded-full",
                statusBgColors[overallStatus],
              )}
            >
              <OverallIcon
                className={cn("h-8 w-8", statusColors[overallStatus])}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-semibold">
                  Overall System Health
                </h2>
                <Badge
                  className={cn(
                    statusBgColors[overallStatus],
                    statusColors[overallStatus],
                  )}
                >
                  {overallStatus}
                </Badge>
              </div>
              <div className="mt-3 flex items-center gap-4">
                <Progress value={overallHealth} className="flex-1 h-2" />
                <span
                  className={cn(
                    "text-xl font-semibold",
                    statusColors[overallStatus],
                  )}
                >
                  {overallHealth}%
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-48 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2">
          {metrics?.map((metric) => {
            const Icon = statusIcons[metric.status];
            const MetricIcon = metricIcons[metric.id] || Activity;

            return (
              <Card key={metric.id} data-testid={`health-card-${metric.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <MetricIcon className="h-4 w-4" />
                      </div>
                      <CardTitle className="text-xl font-medium">
                        {metric.name}
                      </CardTitle>
                    </div>
                    <Badge
                      className={cn(
                        statusBgColors[metric.status],
                        statusColors[metric.status],
                      )}
                    >
                      {metric.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {metric.description}
                    </p>
                    <div className="flex items-center gap-4">
                      <Progress value={metric.value} className="flex-1 h-2" />
                      <span
                        className={cn(
                          "text-lg font-semibold",
                          statusColors[metric.status],
                        )}
                      >
                        {metric.value}%
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Last updated:{" "}
                      {new Date(metric.lastUpdated).toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Cpu className="h-5 w-5" />
            Aeos AI Intelligence Layer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Aeos is the AI system that personalizes wellness rituals for each
              user. Admins can monitor its health but cannot modify individual
              user recommendations to preserve personalization integrity.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="p-4 rounded-md bg-muted/50">
                <p className="text-sm font-medium">Personalization Accuracy</p>
                <p className="text-2xl font-semibold text-green-600 dark:text-green-400 mt-1">
                  94%
                </p>
              </div>
              <div className="p-4 rounded-md bg-muted/50">
                <p className="text-sm font-medium">Ritual Completion Rate</p>
                <p className="text-2xl font-semibold text-blue-600 dark:text-blue-400 mt-1">
                  78%
                </p>
              </div>
              <div className="p-4 rounded-md bg-muted/50">
                <p className="text-sm font-medium">User Satisfaction</p>
                <p className="text-2xl font-semibold text-purple-600 dark:text-purple-400 mt-1">
                  4.6/5
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
